/**
 * Vue Animation Engine
 *
 * Provides a simple interface to use the reusable animation engine in Vue components
 * Based on the working implementation from Splash.vue
 */

import { ref, onUnmounted } from 'vue'
import { AnimationEngine, type AnimationConfig } from '../utils/animation-engine'

export interface UseAnimationEngineOptions {
    containerSelector?: string
    elementSelector?: string
    enableLogging?: boolean
    effectDefaults?: {
        scale: { from: number; to: number; speed: number }
        opacity: { from: number; to: number; speed: number }
        blur: { from: number; to: number; speed: number }
        color: { from: string; to: string; speed: number }
    }
    autoStart?: boolean
    rotationDirections?: ('clockwise' | 'anticlockwise')[] // Array of rotation directions for each object
    rotationSpeed?: number // Rotation speed per frame for all objects
    animationSpeed?: number // Overall animation speed multiplier (low = slow, high = fast)
}

export function useAnimationEngine(options: UseAnimationEngineOptions = {}) {
    const engine = ref<AnimationEngine | null>(null)
    const isInitialized = ref(false)
    const isRunning = ref(false)

    const defaultOptions = {
        containerSelector: '.pink-half-circles--full-width',
        elementSelector: '.plus',
        enableLogging: true,
        effectDefaults: {
            scale: { from: 0.3, to: 1.2, speed: 3 },
            opacity: { from: 0.2, to: 0.9, speed: 3 },
            blur: { from: 8, to: 0, speed: 3 },
            color: { from: '#a250d6', to: '#ffffff', speed: 3 }
        },
        autoStart: true,
        rotationDirections: undefined as ('clockwise' | 'anticlockwise')[] | undefined,
        rotationSpeed: 1.0,
        animationSpeed: 1.0
    }

    const config = { ...defaultOptions, ...options }

    /**
     * Initialize the animation engine
     */
    const initialize = (): boolean => {
        try {
            // Create engine configuration based on working Splash.vue implementation
            const engineConfig: AnimationConfig = {
                containerSelector: config.containerSelector,
                elementSelector: config.elementSelector,
                enableLogging: config.enableLogging,
                effectDefaults: config.effectDefaults,
                autoStart: config.autoStart,
                rotationDirections: config.rotationDirections,
                rotationSpeed: config.rotationSpeed,
                animationSpeed: config.animationSpeed
            }

            // Initialize engine
            engine.value = new AnimationEngine(engineConfig)
            const success = engine.value.initialize()

            if (success) {
                isInitialized.value = true
                isRunning.value = engine.value && config.autoStart

                if (config.enableLogging) {
                    console.log('✅ Animation engine initialized via composable')
                }
            }

            return success

        } catch (error) {
            console.error('❌ Failed to initialize animation engine via composable:', error)
            return false
        }
    }

    /**
     * Start the animation
     */
    const start = (): void => {
        if (engine.value && isInitialized.value) {
            engine.value.start()
            isRunning.value = true
        }
    }

    /**
     * Stop the animation
     */
    const stop = (): void => {
        if (engine.value) {
            engine.value.stop()
            isRunning.value = false
        }
    }

    /**
     * Toggle the animation
     */
    const toggle = (): void => {
        if (engine.value && isInitialized.value) {
            engine.value.toggle()
            isRunning.value = !isRunning.value
        }
    }

    /**
     * Setup keyboard controls for development (Ctrl+Space to toggle)
     */
    const setupKeyboardControls = (): void => {
        if (engine.value && isInitialized.value) {
            engine.value.setupKeyboardControls()
        }
    }

    // Cleanup on unmount
    onUnmounted(() => {
        if (engine.value) {
            engine.value.destroy()
            engine.value = null
        }
        isInitialized.value = false
        isRunning.value = false
    })

    return {
        // State
        isInitialized,
        isRunning,

        // Methods
        initialize,
        start,
        stop,
        toggle,
        setupKeyboardControls
    }
}
